// Second-opinion review panel (single answers) and consensus panel
// (compare mode). Module scope so App re-renders never remount them.
import { useState } from "react";

const short = (id) => String(id || "").split("/").pop();

export function SecondOpinionPanel({ data, S, Markdown, onDismiss }) {
  const [showOwn, setShowOwn] = useState(false);
  if (!data) return null;
  const r = data.result;
  const meta = {
    agree: { label: "Agrees", col: S.success || "#10b981", icon: "✓" },
    partially_agree: { label: "Partly agrees", col: S.warning || "#f59e0b", icon: "≈" },
    disagree: { label: "Disagrees", col: S.danger, icon: "✕" },
  }[(r && r.verdict) || ""] || { label: "Review", col: S.text3, icon: "?" };
  return (
    <div className="cf-second-opinion" style={{ marginTop: 10, border: `1px solid ${data.loading ? S.border2 : meta.col + "66"}`, borderRadius: 12, background: S.cardBg, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: data.loading || data.error ? "none" : `1px solid ${S.border}` }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: S.text2 }}>Second opinion</span>
        {data.reviewer && <span style={{ fontSize: 10, fontFamily: "monospace", color: S.text3 }}>by {short(data.reviewer)}</span>}
        <span style={{ flex: 1 }} />
        {!data.loading && r && r.verdict && (
          <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10, background: meta.col + "1f", color: meta.col }}>
            {meta.icon} {meta.label}{r.confidence != null ? ` · ${r.confidence}% confident` : ""}
          </span>
        )}
        {!data.loading && onDismiss && <button onClick={onDismiss} title="Dismiss" style={{ background: "none", border: "none", color: S.text3, cursor: "pointer", fontSize: 14 }}>×</button>}
      </div>
      {data.loading ? (
        <div style={{ padding: "10px 12px", fontSize: 12, color: S.text3 }}>Asking an independent model to check this answer…</div>
      ) : data.error ? (
        <div style={{ padding: "10px 12px", fontSize: 12, color: S.danger }}>{data.error}</div>
      ) : r ? (
        <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8, fontSize: 12.5, color: S.text }}>
          {r.summary && <div style={{ lineHeight: 1.55 }}>{r.summary}</div>}
          {r.issues.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {r.issues.map((x, i) => (
                <div key={i} style={{ padding: "7px 10px", borderRadius: 8, background: S.inputBg, borderLeft: `3px solid ${meta.col}`, lineHeight: 1.5 }}>
                  {x.claim && <div style={{ color: S.text3, fontSize: 11.5 }}>“{x.claim}”</div>}
                  {x.problem && <div><b>Issue:</b> {x.problem}</div>}
                  {x.correction && <div><b>Correction:</b> {x.correction}</div>}
                </div>
              ))}
            </div>
          )}
          {r.strengths.length > 0 && <div style={{ fontSize: 11.5, color: S.text3 }}>Got right: {r.strengths.join(" · ")}</div>}
          {r.ownAnswer && (
            <div>
              <button onClick={() => setShowOwn((v) => !v)} style={{ background: "none", border: "none", color: S.accent, cursor: "pointer", fontSize: 11.5, padding: 0, fontFamily: "inherit" }}>
                {showOwn ? "Hide" : "Show"} the reviewer's own answer
              </button>
              {showOwn && <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px dashed ${S.border2}` }}>{Markdown ? <Markdown text={r.ownAnswer} /> : r.ownAnswer}</div>}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function ConsensusPanel({ data, labels, S, onDismiss }) {
  if (!data) return null;
  const r = data.result;
  const meta = {
    high: { label: "High agreement", col: S.success || "#10b981" },
    mixed: { label: "Mixed agreement", col: S.warning || "#f59e0b" },
    low: { label: "Low agreement", col: S.danger },
  }[(r && r.consensus) || ""] || { label: "Agreement check", col: S.text3 };
  const name = (n) => labels[n - 1] || `Answer ${n}`;
  const sec = (t) => <div style={{ fontSize: 10, fontFamily: "monospace", color: S.text3, textTransform: "uppercase", margin: "6px 0 4px" }}>{t}</div>;
  return (
    <div className="cf-consensus" style={{ marginTop: 16, border: `1px solid ${data.loading ? S.border2 : meta.col + "66"}`, borderRadius: 14, background: S.cardBg, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderBottom: `1px solid ${S.border}` }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: S.text2 }}>🧭 Do the models agree?</span>
        <span style={{ flex: 1 }} />
        {!data.loading && r && r.consensus && <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10, background: meta.col + "1f", color: meta.col }}>{meta.label}</span>}
        {!data.loading && onDismiss && <button onClick={onDismiss} title="Dismiss" style={{ background: "none", border: "none", color: S.text3, cursor: "pointer", fontSize: 14 }}>×</button>}
      </div>
      <div style={{ padding: "10px 14px", fontSize: 12.5, color: S.text, lineHeight: 1.55 }}>
        {data.loading ? <span style={{ color: S.text3 }}>Comparing the answers claim by claim…</span>
          : data.error ? <span style={{ color: S.danger }}>{data.error}</span>
          : r ? (
            <>
              {r.agreed.length > 0 && (<>{sec("All agree")}<ul style={{ margin: 0, paddingLeft: 18 }}>{r.agreed.map((a, i) => <li key={i}>{a}</li>)}</ul></>)}
              {r.disputed.length > 0 && (<>{sec("Where they differ")}
                {r.disputed.map((d, i) => (
                  <div key={i} style={{ padding: "7px 10px", borderRadius: 8, background: S.inputBg, marginBottom: 6, borderLeft: `3px solid ${S.warning || "#f59e0b"}` }}>
                    <div style={{ fontWeight: 700 }}>{d.point}</div>
                    {d.positions.map((p, k) => <div key={k}><span style={{ color: S.text3 }}>{name(p.answer)}:</span> {p.says}</div>)}
                    {d.likelyCorrect && <div style={{ color: S.text2, marginTop: 2 }}><b>Better supported:</b> {d.likelyCorrect}</div>}
                  </div>
                ))}</>)}
              {r.unique.length > 0 && (<>{sec("Only one model mentioned")}<ul style={{ margin: 0, paddingLeft: 18 }}>{r.unique.map((u, i) => <li key={i}><span style={{ color: S.text3 }}>{name(u.answer)}:</span> {u.insight}</li>)}</ul></>)}
              {r.recommendation && <div style={{ marginTop: 8, fontWeight: 600 }}>{r.recommendation}</div>}
            </>
          ) : null}
      </div>
    </div>
  );
}
